/**
 * MarkdownModeContentComponentDiff — experimental variant of
 * `MarkdownModeContentComponent` that writes source-view edits back to the
 * live ProseMirror doc as surgical `Step`s (via `@atlassian/content-reconciler`) instead of a
 * single full-document `replaceWith`.
 *
 * Selected by the plugin when `platform_editor_md-mode-use-content-reconciler`
 * is enabled. All other behaviour — snapshot, dispatch tap, source/preview
 * rendering, format-state plumbing — matches the legacy component.
 *
 * Differences vs `MarkdownModeContentComponent`:
 *  - `handleSourceMarkdownChange` and `handleMarkdownCommit` call
 *    `applyDiffSteps(editorView, adfNode)` instead of building a
 *    `replaceWith` transaction.
 *  - Annotations are passed to `@atlassian/content-reconciler` as ignored mark types
 *    (`{ ignoreMarkTypes: ['annotation'] }`). The library then:
 *      · doesn't fork prefix/suffix or similarity decisions on annotations,
 *      · inherits annotation marks into Replace/Insert slices from the old
 *        doc, so text edited inside an annotated span keeps the annotation
 *        without an explicit re-paint,
 *      · never emits `AddMark`/`RemoveMark` for annotation marks.
 *    Cross-boundary edits (an edit that spans two annotations, or partially
 *    overlaps one) are still ambiguous — the inheritance rule picks a side.
 *    Snapshot-time annotation ranges are passed to the source view so
 *    highlights render, and `onAnnotationClick` opens the comment popup.
 *  - Collab-safe. Steps are diffed against `seedPmDocRef` and rebased
 *    through `interveningMappingRef` (non-own txs since the seed). Both
 *    refs reset on snapshot and after every successful writeback.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { Mapping } from '@atlaskit/editor-prosemirror/transform';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import type { MarkdownSourceAnnotationRange } from '@atlassian/mdast-util-from-pm/types';

import type {
	MarkdownModePlugin,
	MarkdownModePluginOptions,
	MarkdownModeView,
} from '../markdownModePluginType';
import type { SourceViewCommand, SourceViewFormatState } from '../source-view-utils';
import { applyDiffSteps, OWN_WRITEBACK_META } from '../utils/apply-diff-steps';
import { adfToMarkdown, markdownToAdf } from '../utils/markdown-conversion';
import { padTrailingEmptyParagraph } from '../utils/pad-trailing-empty-paragraph';

import { MarkdownSourceView } from './MarkdownSourceView';
const EMPTY_ADF_DOC: DocNode = { type: 'doc', version: 1, content: [] };

type MarkdownModeContentComponentDiffProps = {
	api: ExtractInjectionAPI<MarkdownModePlugin> | undefined;
	config: MarkdownModePluginOptions;
	currentView: MarkdownModeView;
	editorView: EditorView;
};

export const MarkdownModeContentComponentDiff = ({
	api,
	editorView,
	config,
	currentView,
}: MarkdownModeContentComponentDiffProps): React.ReactElement | null => {
	if (!config.isMarkdownMode) {
		return null;
	}

	return (
		<MarkdownModeOrchestratorDiff
			api={api}
			markdownView={currentView}
			editorView={editorView}
			config={config}
		/>
	);
};

// -------------------------------------------------------------------
// Inner orchestrator — only mounts when isMarkdownMode is true.
// -------------------------------------------------------------------

type OrchestratorProps = {
	api: ExtractInjectionAPI<MarkdownModePlugin> | undefined;
	config: MarkdownModePluginOptions;
	editorView: EditorView;
	markdownView: MarkdownModeView;
};

const MarkdownModeOrchestratorDiff = ({
	api,
	markdownView,
	editorView,
	config,
}: OrchestratorProps) => {
	// ---------------------------------------------------------------
	// State
	// ---------------------------------------------------------------
	const [adfDoc, setAdfDoc] = useState<DocNode>(EMPTY_ADF_DOC);
	const [snapshotRevision, setSnapshotRevision] = useState(0);
	const [liveAdfDoc, setLiveAdfDoc] = useState<DocNode | undefined>(undefined);
	// Pre-computed markdown for the snapshot (platform_editor_md_source_sync_snapshot).
	const [seedMarkdown, setSeedMarkdown] = useState<string | undefined>(undefined);
	// Pushed to MarkdownSourceView after every successful own-writeback so CM6
	// can re-seed (absorbs any remote content that was folded in via rebase).
	const [postWritebackAdfDoc, setPostWritebackAdfDoc] = useState<DocNode | undefined>(undefined);
	const liveDocDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const dispatchTapInstalledRef = useRef(false);
	// Collab-safe diff baseline: steps are computed against `seedPmDocRef` and
	// rebased through `interveningMappingRef`. Both reset on snapshot and after
	// every successful writeback.
	const seedPmDocRef = useRef<PMNode | null>(null);
	const interveningMappingRef = useRef<Mapping>(new Mapping());
	// Stable ref for serializeFileMedia so effects and callbacks can read the
	// latest value without taking it as a dep (unstable parent references would
	// otherwise re-fire the snapshot effect and wipe the refs above).
	const serializeFileMediaRef = useRef(config.serializeFileMedia);
	serializeFileMediaRef.current = config.serializeFileMedia;
	// Annotation ranges extracted at snapshot time — passed to the source view
	// purely so highlight decorations render and click-to-open works. The diff
	// writeback path does NOT re-apply annotation marks (see file header).
	const [annotationRanges, setAnnotationRanges] = useState<MarkdownSourceAnnotationRange[]>([]);

	// ---------------------------------------------------------------
	// Snapshot — synchronously read the current doc from PM.
	// ---------------------------------------------------------------
	const snapshotAdf = useCallback((): DocNode | null => {
		if (!editorView || editorView.isDestroyed) {
			return null;
		}
		return editorView.state.doc.toJSON() as DocNode;
	}, [editorView]);

	// Snapshot whenever the user transitions away from preview (the WYSIWYG editor).
	useEffect(() => {
		if (markdownView === 'preview') {
			return;
		}
		const snap = snapshotAdf();
		if (snap && snap.type === 'doc') {
			setAdfDoc(snap);
			setSnapshotRevision((r) => r + 1);
			setLiveAdfDoc(undefined);
			setPostWritebackAdfDoc(undefined);
			seedPmDocRef.current = editorView.state.doc;
			interveningMappingRef.current = new Mapping();

			// Convert the snapshot to markdown a SINGLE time here (when either path
			// needs it) and reuse for both the source-view seed and annotation ranges.
			if (
				fg('platform_editor_md_source_sync_snapshot') ||
				fg('platform_editor_use_mdast_utils_conversion')
			) {
				try {
					const schema = editorView.state.schema as Schema;
					const pmDoc = editorView.state.doc;
					const { markdown, annotations } = adfToMarkdown(pmDoc, schema, {
						serializeFileMedia: serializeFileMediaRef.current,
					});
					if (fg('platform_editor_md_source_sync_snapshot')) {
						setSeedMarkdown(markdown);
					}
					if (fg('platform_editor_use_mdast_utils_conversion')) {
						setAnnotationRanges(annotations);
					}
				} catch {
					if (fg('platform_editor_md_source_sync_snapshot')) {
						setSeedMarkdown(undefined);
					}
					if (fg('platform_editor_use_mdast_utils_conversion')) {
						setAnnotationRanges([]);
					}
				}
			}
		}
	}, [markdownView, snapshotAdf, editorView]);

	// ---------------------------------------------------------------
	// Dispatch tap — push debounced live updates to source/preview.
	// ---------------------------------------------------------------
	useEffect(() => {
		if (markdownView === 'preview') {
			return;
		}
		if (!editorView || editorView.isDestroyed) {
			return;
		}
		if (dispatchTapInstalledRef.current) {
			return;
		}

		const original = editorView.props.dispatchTransaction;

		const tap = (tr: Transaction) => {
			// Always run the original dispatcher first.
			if (original) {
				original.call(editorView, tr);
			} else {
				editorView.updateState(editorView.state.apply(tr));
			}

			if (!tr.docChanged) {
				return;
			}

			if (tr.getMeta(OWN_WRITEBACK_META)) {
				return; // our writeback — don't accumulate, don't push to source view
			}

			for (const map of tr.mapping.maps) {
				interveningMappingRef.current.appendMap(map);
			}

			// Debounce: coalesce rapid keystrokes.
			if (liveDocDebounceRef.current !== null) {
				clearTimeout(liveDocDebounceRef.current);
			}
			liveDocDebounceRef.current = setTimeout(() => {
				liveDocDebounceRef.current = null;
				if (!editorView || editorView.isDestroyed) {
					return;
				}
				setLiveAdfDoc(editorView.state.doc.toJSON() as DocNode);
			}, 150);
		};

		editorView.setProps({ dispatchTransaction: tap });
		dispatchTapInstalledRef.current = true;

		return () => {
			if (liveDocDebounceRef.current !== null) {
				clearTimeout(liveDocDebounceRef.current);
				liveDocDebounceRef.current = null;
			}
			// Restore the original dispatcher so the tap closure (and the React
			// fiber it captures via setLiveAdfDoc) can be GC'd, and so a future
			// editorView change re-installs cleanly.
			if (!editorView.isDestroyed) {
				editorView.setProps({ dispatchTransaction: original });
			}
			dispatchTapInstalledRef.current = false;
		};
	}, [markdownView, editorView]);

	useEffect(() => {
		return () => {
			if (liveDocDebounceRef.current !== null) {
				clearTimeout(liveDocDebounceRef.current);
				liveDocDebounceRef.current = null;
			}
		};
	}, []);

	// ---------------------------------------------------------------
	// Writeback handlers
	// ---------------------------------------------------------------

	// Same stable-ref pattern for the dev-only step-observer callback.
	const onDiffStepsAppliedRef = useRef(config.onDiffStepsApplied);
	onDiffStepsAppliedRef.current = config.onDiffStepsApplied;

	/**
	 * Called when the user clicks an annotation highlight in the CodeMirror
	 * source view. Mirrors the click handler in the annotation plugin's
	 * inline-comment.ts: checks the annotation is unresolved, then dispatches
	 * setSelectedAnnotation (or setPendingSelectedAnnotation when the annotation
	 * manager is enabled) on the live ProseMirror editor so that the comment
	 * popup / comments panel opens, exactly as in the WYSIWYG view.
	 *
	 * Gated behind platform_editor_use_mdast_utils_conversion (annotation ranges
	 * are only surfaced in source view under that gate).
	 */
	const handleAnnotationClick = useCallback(
		(annotationId: string) => {
			if (!editorView || editorView.isDestroyed) {
				return;
			}
			// Read annotation plugin state via api.annotation.sharedState — avoids
			// importing internal annotation plugin utilities directly and works
			// correctly with the pre-built dist in the browser dev environment.
			const annotationPluginState = api?.annotation?.sharedState.currentState();
			if (!annotationPluginState) {
				return;
			}
			const { annotations, isAnnotationManagerEnabled } = annotationPluginState;
			// Only react to unresolved annotations (matches WYSIWYG click handler).
			const isUnresolved = annotations && annotations[annotationId] === false;
			if (!isUnresolved) {
				return;
			}
			if (isAnnotationManagerEnabled) {
				api?.annotation?.actions.setPendingSelectedAnnotation(annotationId)(
					editorView.state,
					editorView.dispatch,
				);
			} else {
				api?.annotation?.actions.setSelectedAnnotation(annotationId)(
					editorView.state,
					editorView.dispatch,
				);
			}
		},
		[editorView, api],
	);

	/**
	 * Forwarded to MarkdownSourceView as `onRegisterCommandDispatch`.
	 * Called on mount with a stable dispatcher that routes CM6 commands to
	 * the live CodeMirror EditorView, and with `null` on unmount.
	 * Registers/unregisters the dispatcher on the markdownMode plugin action
	 * so toolbar buttons can call `api.markdownMode.actions.dispatchSourceCommand`.
	 * Only wired when `platform_editor_markdown_compatible_toolbar` is enabled.
	 */
	// Stable ref to the CM6 dispatcher — populated when MarkdownSourceView
	// mounts and cleared when it unmounts.
	const sourceDispatchRef = useRef<((cmd: SourceViewCommand) => boolean) | null>(null);

	const handleRegisterCommandDispatch = useCallback(
		(dispatcher: ((cmd: SourceViewCommand) => boolean) | null) => {
			sourceDispatchRef.current = dispatcher;
			api?.markdownMode?.actions.registerSourceCommandDispatch(dispatcher);
			if (dispatcher === null) {
				api?.markdownMode?.actions.setSourceFormatState(null);
			}
		},
		[api],
	);

	// Re-register the dispatcher whenever api changes (preset rebuild resets
	// the plugin closure, clearing sourceCommandDispatch to null).
	useEffect(() => {
		if (!fg('platform_editor_markdown_compatible_toolbar')) {
			return;
		}
		if (sourceDispatchRef.current) {
			api?.markdownMode?.actions.registerSourceCommandDispatch(sourceDispatchRef.current);
		}
	}, [api]);

	const handleSourceFormatStateChange = useCallback(
		(state: SourceViewFormatState) => {
			api?.markdownMode?.actions.setSourceFormatState(state);
		},
		[api],
	);

	/**
	 * Called by MarkdownSourceView on every debounced source change (Tier 2).
	 * Parses markdown back to ADF and applies the delta to the live PM doc via
	 * surgical `Step`s computed by `@atlassian/content-reconciler`.
	 */
	const handleSourceMarkdownChange = useCallback(
		(markdown: string) => {
			if (!editorView || editorView.isDestroyed) {
				return;
			}
			const seedDoc = seedPmDocRef.current;
			if (!seedDoc) {
				return;
			}
			let adfNode: PMNode;
			try {
				adfNode = markdownToAdf(markdown, editorView.state.schema as Schema);
			} catch (err) {
				// Bound interveningMapping growth while the user iterates on invalid
				// markdown; next valid input diffs against the post-remote baseline.
				seedPmDocRef.current = editorView.state.doc;
				interveningMappingRef.current = new Mapping();
				return;
			}

			// Early-out + re-seed: if live PM already serializes to the requested
			// markdown, the diff is a no-op and current PM is the right new seed.
			try {
				const { markdown: liveMarkdown } = adfToMarkdown(
					editorView.state.doc,
					editorView.state.schema as Schema,
					{
						serializeFileMedia: serializeFileMediaRef.current,
					},
				);
				if (liveMarkdown === markdown) {
					seedPmDocRef.current = editorView.state.doc;
					interveningMappingRef.current = new Mapping();
					return;
				}
			} catch (err) {
				// fall through to diff path
			}

			// Capture before dispatch so the dev callback sees the diff baseline.
			const oldDocForCallback = editorView.state.doc;
			const paddedAdfNode = padTrailingEmptyParagraph(oldDocForCallback, adfNode);
			try {
				const tr = applyDiffSteps(
					editorView,
					seedDoc,
					paddedAdfNode,
					interveningMappingRef.current,
					{ ignoreMarkTypes: ['annotation'] },
				);
				if (tr) {
					seedPmDocRef.current = editorView.state.doc;
					interveningMappingRef.current = new Mapping();
					setPostWritebackAdfDoc(editorView.state.doc.toJSON() as DocNode);
					onDiffStepsAppliedRef.current?.({
						markdown,
						newDoc: paddedAdfNode,
						oldDoc: oldDocForCallback,
						source: 'live',
						steps: tr.steps,
					});
				}
			} catch (err) {
				// Re-seed so a positional rebase failure is recoverable: next flush
				// diffs against the live doc; user's typing wins. (Note: CM6's
				// seedMarkdownRef is NOT bumped here — the source view keeps the
				// user's intent intact and just sees a wider userDiff next round.)
				seedPmDocRef.current = editorView.state.doc;
				interveningMappingRef.current = new Mapping();
			}
		},
		[editorView],
	);

	/**
	 * Called by MarkdownSourceView on unmount / revision change when the
	 * user actually edited the buffer (Tier 0 — batch commit). Applies the
	 * delta as surgical steps via the same path as handleSourceMarkdownChange.
	 */
	const handleMarkdownCommit = useCallback(
		(finalMarkdown: string) => {
			if (!editorView || editorView.isDestroyed) {
				return;
			}
			const seedDoc = seedPmDocRef.current;
			if (!seedDoc) {
				return;
			}
			try {
				// MarkdownSourceView's per-seed guard only catches open-then-close-
				// without-typing. Live WYSIWYG updates flow into the source buffer via
				// the dispatch tap, so finalMarkdown can differ from the seed yet still
				// round-trip to the current live doc.
				try {
					const { markdown: liveMarkdown } = adfToMarkdown(
						editorView.state.doc,
						editorView.state.schema as Schema,
						{
							serializeFileMedia: serializeFileMediaRef.current,
						},
					);
					if (liveMarkdown === finalMarkdown) {
						seedPmDocRef.current = editorView.state.doc;
						interveningMappingRef.current = new Mapping();
						return;
					}
				} catch (err) {
					// fall through to diff path
				}
				const adfNode = markdownToAdf(finalMarkdown, editorView.state.schema as Schema);
				const oldDocForCallback = editorView.state.doc;
				const paddedAdfNode = padTrailingEmptyParagraph(oldDocForCallback, adfNode);
				const tr = applyDiffSteps(
					editorView,
					seedDoc,
					paddedAdfNode,
					interveningMappingRef.current,
					{ ignoreMarkTypes: ['annotation'] },
				);
				if (tr) {
					seedPmDocRef.current = editorView.state.doc;
					interveningMappingRef.current = new Mapping();
					setPostWritebackAdfDoc(editorView.state.doc.toJSON() as DocNode);
					onDiffStepsAppliedRef.current?.({
						markdown: finalMarkdown,
						newDoc: paddedAdfNode,
						oldDoc: oldDocForCallback,
						source: 'commit',
						steps: tr.steps,
					});
				}
			} catch (err) {
				// Same recovery as handleSourceMarkdownChange.
				seedPmDocRef.current = editorView.state.doc;
				interveningMappingRef.current = new Mapping();
				// eslint-disable-next-line no-console
				console.warn(
					'[MarkdownModeContentComponentDiff] Failed to apply markdown source edits back to the editor; discarding change.',
					err,
				);
			}
		},
		[editorView],
	);

	// Hide the WYSIWYG ProseMirror DOM in syntax and split-view modes. The
	// platform's `FullPageContentArea` attempts this via
	// `hideEditorContentAreaProsemirrorStyle`, but that style is gated on the
	// `cc-markdown-mode` experiment AND on the plugin's PM state being in sync
	// — either can fail on first paint, leaving both the source CodeMirror
	// and the underlying ProseMirror visible (with floating toolbars leaking
	// through). Scoped to this plugin's `editorView.dom`; original `display`
	// is restored on cleanup. Blur on leaving preview dismisses
	// selection-anchored floating UI (code-block toolbar, table controls).
	useEffect(() => {
		if (!editorView || editorView.isDestroyed) {
			return;
		}
		if (markdownView === 'preview') {
			return;
		}

		const dom = editorView.dom;
		const originalDisplay = dom.style.display;
		dom.style.display = 'none';

		// Guard against blur throwing on a destroyed view.
		try {
			if (editorView.hasFocus()) {
				dom.blur();
			}
		} catch {
			// no-op
		}

		return () => {
			if (!editorView.isDestroyed) {
				dom.style.display = originalDisplay;
			}
		};
	}, [markdownView, editorView]);

	// ---------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------
	const isPreview = markdownView === 'preview';

	if (isPreview) {
		return null;
	}

	const PreviewComponent = config.previewComponent;
	// Resolve each gate once per render so paired props stay consistent and
	// we don't fire duplicate exposures.
	const mdastUtilsConversionEnabled = fg('platform_editor_use_mdast_utils_conversion');
	const compatibleToolbarEnabled = fg('platform_editor_markdown_compatible_toolbar');

	return (
		<>
			{/* Under sync-snapshot, wait for the first real snapshot (revision > 0) so
			    CodeMirror doesn't mount against the empty placeholder doc. */}
			{markdownView === 'syntax' &&
				(snapshotRevision > 0 || !fg('platform_editor_md_source_sync_snapshot')) && (
				<MarkdownSourceView
					contentId={config.contentId}
					initialAdfDoc={adfDoc}
					seedMarkdown={seedMarkdown}
					schema={editorView.state.schema}
					revision={snapshotRevision}
					liveAdfDoc={liveAdfDoc}
					postWritebackAdfDoc={postWritebackAdfDoc}
					onSourceMarkdownChange={handleSourceMarkdownChange}
					onMarkdownCommit={handleMarkdownCommit}
					serializeFileMedia={config.serializeFileMedia}
					annotationRanges={mdastUtilsConversionEnabled ? annotationRanges : undefined}
					onAnnotationClick={mdastUtilsConversionEnabled ? handleAnnotationClick : undefined}
					onRegisterCommandDispatch={
						compatibleToolbarEnabled ? handleRegisterCommandDispatch : undefined
					}
					onSourceFormatStateChange={
						compatibleToolbarEnabled ? handleSourceFormatStateChange : undefined
					}
				/>
			)}
			{markdownView === 'split-view' && PreviewComponent && (
				<PreviewComponent key={`preview-${snapshotRevision}`} adfDoc={liveAdfDoc ?? adfDoc} />
			)}
		</>
	);
};
